import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { ProjectSummary } from '@/types'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { PageHeader } from '@/components/generals/page-header'
import { ProjectsTable } from '@/components/projects/projects-table'
import { getProjects } from '@/lib/content'
import { shouldRethrow } from '@/lib/db'
import { countWord, groupDigits, pad2 } from '@/lib/format'

interface ProjectsPageProps {
    projects: Array<ProjectSummary>
}

const ProjectsPage: FC<ProjectsPageProps> = ({ projects }): ReactNode => {
    const cities = new Set(projects.map(project => project.city)).size
    const area = projects.reduce((total, project) => total + project.areaM2, 0)
    const live = projects.filter(project => project.status.trim().toLowerCase() !== 'in progress').length

    return (
        <Layout>
            <Seo
                title='Projects'
                description='Built, measured and still reporting: the index of Norrvik projects, from Hallmark Library in Umeå to the Vantaa Station Roof, each with a live point-cloud model.'
                figure='Fig. 01 / Projects / Index'
                shape='hall'
            />

            <PageHeader
                fig='01'
                title='Projects'
                detail='Index'
                note='Filter by structure / sort by year or area'
                lines={[`${countWord(projects.length)} buildings,`, 'still reporting.']}
                intro={(
                    <p>
                        Every building below has a live model that kept running after handover. Open a row for its point cloud, its facts and two years of monitored energy use.
                    </p>
                )}
                readouts={[
                    { label: 'Records', value: pad2(projects.length) },
                    { label: 'Cities', value: pad2(cities) },
                    { label: 'Floor area', value: `${groupDigits(area)} m²` },
                    { label: 'Monitored live', value: `${pad2(live)} / ${pad2(projects.length)}` }
                ]}
            />

            <section aria-label='Project index' className='pb-20 pt-10 desk:pb-28'>
                {projects.length > 0 ? (
                    <ProjectsTable projects={projects} />
                ) : (
                    <div className='mx-gutter border border-hairline px-6 py-16 text-center'>
                        <p className='hud text-mute'>
                            The project index is being refreshed. Please check back in a moment.
                        </p>
                    </div>
                )}
            </section>
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<ProjectsPageProps> = async () => {
    try {
        const projects = await getProjects()

        return { props: { projects }, revalidate: 60 }
    }
    catch (error) {
        console.error('Projects could not be loaded:', error instanceof Error ? error.message : error)

        if (shouldRethrow()) throw error

        return { props: { projects: [] }, revalidate: 30 }
    }
}

export default ProjectsPage
